import { useState } from 'react';
import { ArrowLeft, ArrowRight, CircleCheck, CircleX, Eye, EyeOff, LoaderCircle, Lock, ShieldCheck } from 'lucide-react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useNotification } from '../../../components/Notifications/NotificationContext';
import { useAuth } from '../../../context/AuthProvider';
import AuthShell from '../AuthShell.jsx';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { resetPassword } = useAuth();

  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      showError('Link inválido ou expirado. Peça um novo.');
      return;
    }
    if (password.length < 8) {
      showError('A senha deve ter no mínimo 8 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      showError('As senhas não coincidem');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, password);
      showSuccess('Senha redefinida com sucesso!');
      navigate('/login');
    } catch (err) {
      showError(err.response?.data?.error || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={
        <>
          Senha nova, <span className="sp-gradient-text">acesso de volta.</span>
        </>
      }
    >
      <h2 className="sp-card-title">Nova senha</h2>
      <p className="sp-card-sub">Escolha uma senha com pelo menos 8 caracteres.</p>

      <form onSubmit={handleSubmit} noValidate>
        <div className="sp-field">
          <label htmlFor="password" className="sp-label">Nova senha</label>
          <div className="sp-input-wrapper">
            <Lock className="sp-input-icon" size={16} aria-hidden="true" />
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoFocus
              autoComplete="new-password"
              placeholder="••••••••"
              disabled={loading}
              className="sp-input"
              style={{ paddingRight: '3rem' }}
            />
            <button
              type="button"
              className="sp-eye-toggle"
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
            >
              {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
            </button>
          </div>
        </div>

        <div className="sp-field">
          <label htmlFor="confirmPassword" className="sp-label">Confirmar nova senha</label>
          <div className="sp-input-wrapper">
            <ShieldCheck className="sp-input-icon" size={16} aria-hidden="true" />
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="••••••••"
              disabled={loading}
              className="sp-input"
            />
          </div>
          {confirmPassword.length > 0 && (
            <div className={`sp-match-hint ${passwordsMatch ? 'ok' : 'bad'}`}>
              {passwordsMatch ? <CircleCheck size={14} aria-hidden="true" /> : <CircleX size={14} aria-hidden="true" />}
              {passwordsMatch ? 'As senhas coincidem' : 'As senhas não coincidem'}
            </div>
          )}
        </div>

        <button type="submit" className="sp-btn-primary" disabled={loading}>
          {loading ? (
            <>
              <LoaderCircle className="sp-spin" size={18} aria-hidden="true" />
              Salvando...
            </>
          ) : (
            <>
              Salvar nova senha
              <ArrowRight className="sp-btn-arrow" size={18} aria-hidden="true" />
            </>
          )}
        </button>
      </form>

      <div className="sp-card-footer">
        <Link to="/login" className="sp-link sp-back-link">
          <ArrowLeft size={16} aria-hidden="true" /> Voltar para o login
        </Link>
      </div>
    </AuthShell>
  );
}
